import { Button, Spin, Typography, List } from 'antd';
import { useCallback, useState, useEffect } from 'react';
import { CHILD_PROVIDER_SITUATION_VALUES, FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import PageMeta from 'components/PageMeta/PageMeta';
import { getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { Banner } from 'components/ui/Banner';
import { Add, Edit, Calendar, Home, UserMultiple, Education, Partnership } from '@carbon/icons-react';
import dayjs from 'dayjs';
import { DATE_FORMAT_SENTENCE } from 'components/AriaDatePicker/AriaDatePicker';
import Card from 'components/Card/Card';
import { familyService } from 'services';
import { useAsync } from 'react-async-hook';
import { getFormattedPhoneNumber, getFullName } from 'utils';
import { getApplicantFirstName, getOtherAdultFirstName } from '../../utils';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';
import { useFlags } from 'launchdarkly-react-client-sdk';

const DAY_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const DAY_ABBR = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
};

function formatScheduleDays(days) {
  if (!days?.length) return '';
  const sorted = [...days].sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b));
  const ranges = [];
  let start = 0;
  for (let i = 1; i <= sorted.length; i++) {
    if (i === sorted.length || DAY_ORDER.indexOf(sorted[i]) !== DAY_ORDER.indexOf(sorted[i - 1]) + 1) {
      ranges.push(
        i - 1 - start >= 1 ? `${DAY_ABBR[sorted[start]]} – ${DAY_ABBR[sorted[i - 1]]}` : DAY_ABBR[sorted[start]],
      );
      start = i;
    }
  }
  return ranges.join(', ');
}

function formatScheduleTime(time) {
  if (!time) return '';
  const [hours, minutes] = time.split(':').map(Number);
  const h = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
  return `${h}:${String(minutes).padStart(2, '0')} ${hours < 12 ? 'AM' : 'PM'}`;
}

function getScheduleGroups(schedule) {
  const code = schedule?.careNeedType?.code;
  const days = schedule?.days;
  if (!days?.length) return [];

  const showMorning = ['BEFORE_SCHOOL', 'BEFORE_AND_AFTER_SCHOOL', 'SUMMER', 'NON_SCHOOL'].includes(code);
  const showAfternoon = ['AFTER_SCHOOL', 'BEFORE_AND_AFTER_SCHOOL'].includes(code);
  const trim = (t) => t?.slice(0, 5) ?? null;

  const getDayTimes = (day) => {
    const morningBlock = day.morning ?? day.summer ?? day.nonSchool ?? null;
    return {
      morningStart: showMorning ? trim(morningBlock?.startTime) : null,
      morningEnd: showMorning ? trim(morningBlock?.endTime) : null,
      afternoonStart: showAfternoon ? trim(day.afternoon?.startTime) : null,
      afternoonEnd: showAfternoon ? trim(day.afternoon?.endTime) : null,
    };
  };

  const groups = new Map();
  days.forEach((day) => {
    const times = getDayTimes(day);
    const key = JSON.stringify(times);
    if (!groups.has(key)) groups.set(key, { times, dayNames: [] });
    groups.get(key).dayNames.push(day.dayOfWeek);
  });

  return [...groups.values()]
    .sort((a, b) => {
      const firstOf = (g) => [...g.dayNames].sort((x, y) => DAY_ORDER.indexOf(x) - DAY_ORDER.indexOf(y))[0];
      return DAY_ORDER.indexOf(firstOf(a)) - DAY_ORDER.indexOf(firstOf(b));
    })
    .map(({ times, dayNames }) => ({
      days: formatScheduleDays(dayNames),
      morningRange:
        times.morningStart && times.morningEnd
          ? `${formatScheduleTime(times.morningStart)} – ${formatScheduleTime(times.morningEnd)}`
          : null,
      afternoonRange:
        times.afternoonStart && times.afternoonEnd
          ? `${formatScheduleTime(times.afternoonStart)} – ${formatScheduleTime(times.afternoonEnd)}`
          : null,
    }));
}

export function FamilyApplicationChildrenSummary({ application, loading, setStep, readOnly = false }) {
  const [educationData, setEducationData] = useState({});
  const [loadingEducation, setLoadingEducation] = useState(false);
  const stepConfig = useAtomValue(stepConfigAtom);
  const { showFamilyApplicationsScheduleOfCare } = useFlags();

  // Fetch education data for all children
  useEffect(() => {
    const fetchEducationData = async () => {
      if (!application?.children?.length) return;

      setLoadingEducation(true);
      try {
        const educationPromises = application?.children?.map(async (child) => {
          if (!child.id) return { childId: child.id, education: [] };

          try {
            const education = await familyService.getSchoolInfo(child.id);
            return { childId: child.id, education: education || [] };
          } catch (error) {
            console.error(`Error fetching education for child ${child.id}:`, error);
            return { childId: child.id, education: [] };
          }
        });

        const educationResults = await Promise.all(educationPromises);
        const educationMap = {};

        educationResults.forEach(({ childId, education }) => {
          if (childId) {
            educationMap[childId] = education;
          }
        });

        setEducationData(educationMap);
      } catch (error) {
        console.error('Error fetching education data:', error);
      } finally {
        setLoadingEducation(false);
      }
    };

    fetchEducationData();
  }, [application?.children]);

  const handleEditChild = useCallback(
    (childIndex) => {
      setStep({
        applicationId: application?.id,
        newStep: `children-basic-info?entity=${childIndex}`,
      });
    },
    [application?.id, setStep],
  );

  const handleAddChild = useCallback(() => {
    const nextChildIndex = application?.children?.length;
    setStep({
      applicationId: application?.id,
      newStep: `children-basic-info?entity=${nextChildIndex}`,
    });
  }, [application?.id, application?.children?.length, setStep]);

  const handleContinue = useCallback(async () => {
    const _goToNextStep = (_newApplication) => {
      const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.CHILDREN_SUMMARY, _newApplication, stepConfig);
      setStep({
        applicationId: _newApplication?.id,
        newStep: nextStep,
      });
    };

    if (readOnly) {
      _goToNextStep(application);
      return;
    }

    _goToNextStep(application);
  }, [application, readOnly, setStep, stepConfig]);

  const getChildAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    // would be either years, months or day
    let age = dayjs().diff(dayjs(dateOfBirth), 'years');
    if (age === 0) {
      age = dayjs().diff(dayjs(dateOfBirth), 'months');

      if (age === 0) {
        age = dayjs().diff(dayjs(dateOfBirth), 'days');
        return `${age} day${age === 1 ? '' : 's'} old`;
      }

      return `${age} month${age === 1 ? '' : 's'} old`;
    }
    return `${age} year${age === 1 ? '' : 's'} old`;
  };

  const getRelationshipLabel = (relationship) => {
    switch (relationship) {
      case 'Child':
        return 'Child';
      case 'Stepchild':
        return 'Stepchild';
      case 'FosterChild':
        return 'Foster Child';
      case 'LegalGuardian':
        return 'Legal Guardian/Ward';
      default:
        return relationship || 'Unknown';
    }
  };

  const getProviderSituationLabel = (providerSituation) => {
    switch (providerSituation) {
      case CHILD_PROVIDER_SITUATION_VALUES.InCare:
        return 'Already paying for child care';
      case CHILD_PROVIDER_SITUATION_VALUES.ProviderIdentified:
        return "Chosen provider but haven't started";
      case CHILD_PROVIDER_SITUATION_VALUES.ReferralNeeded:
        return 'Need help finding a provider';
      default:
        return 'Not specified';
    }
  };

  const getLivingSituationLabel = (livingSituation) => {
    switch (livingSituation) {
      case 'WithApplicant':
        return `Lives with ${getApplicantFirstName(application?.applicant)}`;
      case 'WithOtherParent':
        return `Lives with ${getOtherAdultFirstName(application?.additionalParents?.[0])}`;
      case 'Shared':
        return 'Lives with both adults';
      case 'Other':
        return 'Lives with someone else';
      default:
        return 'Not specified';
    }
  };

  const { result: providers = {} } = useAsync(async () => {
    if (!application?.children) return null;

    try {
      const _providers = await familyService.getApplicationProviders(application.id);

      const _result = {};
      application.children.forEach((child) => {
        _result[child.id] = _providers.filter((p) => p.childId === child.id);
      });
      return _result;
    } catch (error) {
      console.error('Error loading providers:', error);
      return {};
    }
  }, [application?.children]);

  const getChildDataItems = (child) => {
    const items = [
      {
        label: (
          <div className="flex items-center gap-2">
            <UserMultiple className="w-4 h-4" />
            <span>Relationship to Service Member</span>
          </div>
        ),
        value: getRelationshipLabel(child.relationToApplicant),
      },
      {
        label: (
          <div className="flex items-center gap-2">
            <Home className="w-4 h-4" />
            <span>Living Situation</span>
          </div>
        ),
        value: getLivingSituationLabel(child.livingSituation),
      },
    ];

    // Care Start Date
    if (child.careStartDate) {
      items.push({
        label: (
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            <span>Care Start Date</span>
          </div>
        ),
        value: dayjs(child.careStartDate).format(DATE_FORMAT_SENTENCE),
      });
    }

    // Provider Situation
    if (child.providerSituation) {
      items.push({
        label: (
          <div className="flex items-center gap-2">
            <Partnership className="w-4 h-4" />
            <span>Provider Situation</span>
          </div>
        ),
        value: `${getProviderSituationLabel(child.providerSituation)}`,
      });
    }

    // Provider Situation
    if (
      child.providerSituation !== CHILD_PROVIDER_SITUATION_VALUES.ReferralNeeded &&
      Array.isArray(providers?.[child.id]) &&
      providers?.[child.id]?.length !== 0
    ) {
      const _remainingProviders = providers[child.id].length - 1;

      items.push({
        label: (
          <div className="flex items-center gap-2">
            <Partnership className="w-4 h-4" />
            <span>Provider Information</span>
          </div>
        ),
        value: `${
          providers[child.id].length === 1
            ? `${providers?.[child.id]?.[0]?.name}${
                providers?.[child.id]?.[0]?.phone
                  ? ` - ${getFormattedPhoneNumber(providers?.[child.id]?.[0]?.phone)}`
                  : ''
              }`
            : `${providers?.[child.id]?.[0]?.name} + ${_remainingProviders} more provider${
                _remainingProviders === 1 ? '' : 's'
              }`
        }`,
      });
    } else if (
      showFamilyApplicationsScheduleOfCare &&
      child.providerSituation === CHILD_PROVIDER_SITUATION_VALUES.ReferralNeeded
    ) {
      // Schedule summary for ReferralNeeded (no provider selected yet)

      const schedule = child.schedules?.[0];
      const scheduleGroups = getScheduleGroups(schedule);

      items.push({
        label: (
          <div className="flex items-center gap-2">
            <Partnership className="w-4 h-4" />
            <span>Schedule of Care</span>
          </div>
        ),
        value: (
          <div className="flex flex-col gap-2">
            {scheduleGroups.length > 0 && (
              <div className="border border-solid border-gray-200 rounded-lg p-3">
                {schedule?.careNeedType?.label && (
                  <div className="font-semibold text-gray-700 mb-2">{schedule?.careNeedType?.label}</div>
                )}

                <div className="flex flex-col gap-2">
                  {scheduleGroups.map((group, i) => (
                    <div key={i} className={i > 0 ? 'pt-2 border-t border-gray-100' : ''}>
                      <div className="text-gray-700 font-semibold">{group.days}</div>
                      {group.morningRange && !group.afternoonRange && (
                        <div className="text-gray-600">{group.morningRange}</div>
                      )}
                      {!group.morningRange && group.afternoonRange && (
                        <div className="text-gray-600">{group.afternoonRange}</div>
                      )}
                      {group.morningRange && group.afternoonRange && (
                        <div className="flex flex-col text-gray-600">
                          <div>Morning: {group.morningRange}</div>
                          <div>Afternoon: {group.afternoonRange}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ),
      });
    }

    // School Information from fetched education data
    const childEducation = educationData[child.id] || [];
    if (child.inSchool && childEducation.length > 0) {
      const education = childEducation[0]; // Get the first education record
      const schoolInfo = [];

      if (education.grade) {
        schoolInfo.push(
          <>
            <span className="font-semibold">Grade:</span> {education.grade}
          </>,
        );
      }

      if (education.schoolName) {
        schoolInfo.push(
          <>
            <span className="font-semibold">School:</span> {education.schoolName}
          </>,
        );
      }

      if (education.address?.formattedAddress) {
        schoolInfo.push(
          <>
            <span className="font-semibold">Address:</span> {education.address.formattedAddress}
          </>,
        );
      }

      if (education.endDate || education.startDate) {
        let schoolYearInfo = '';
        if (education.endDate) schoolYearInfo += `Ends ${dayjs(education.endDate).format(DATE_FORMAT_SENTENCE)}`;
        if (education.endDate && education.startDate) schoolYearInfo += ', ';
        if (education.startDate) schoolYearInfo += `Starts ${dayjs(education.startDate).format(DATE_FORMAT_SENTENCE)}`;
        const schoolYear = (
          <>
            <span className="font-semibold">School Year:</span> {schoolYearInfo}
          </>
        );
        schoolInfo.push(schoolYear);
      }

      if (schoolInfo.length > 0) {
        items.push({
          label: (
            <div className="flex items-center gap-2">
              <Education className="w-4 h-4" />
              <span>School Information</span>
            </div>
          ),
          value: (
            <div className="flex flex-col gap-1">
              {schoolInfo.map((info, index) => (
                <div key={`info-${index}`}>{info}</div>
              ))}
            </div>
          ),
        });
      }
    }

    return items;
  };

  if (loading || loadingEducation) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <>
      <PageMeta title="Children Summary" />

      <div className="p-7 pt-0">
        <Banner question="Children who need care" className="my-12" />

        {!application?.children?.length ? (
          <Card className="text-center p-8">
            <Typography.Title level={4} className="text-gray-500 mb-4">
              No children added yet
            </Typography.Title>
            <Typography.Text className="text-gray-600 mb-6 block">
              You haven't added any children to your application yet. Click the button below to add your first child
            </Typography.Text>
            <Button
              type="primary"
              icon={<Add className="w-7 h-7 mb-[-2px] ml-[-5px] mr-[-4px]" />}
              size="large"
              onClick={handleAddChild}
              data-testid="add-first-child-button"
              className="m-auto"
            >
              Add Child
            </Button>
          </Card>
        ) : (
          <div className="space-y-8">
            {application?.children?.map((child, index) => (
              <Card
                key={index}
                title={
                  <div className="flex items-center gap-5 my-6 md:my-8">
                    <div className="flex items-center justify-center w-12 h-12 bg-blue-200 bg-opacity-80 rounded-full text-blue-600 font-bold text-xl flex-shrink-0ap">
                      {index + 1}
                    </div>

                    <div className="flex flex-col gap-1">
                      <div className="text-lg font-semibold text-gray-900">{getFullName(child)}</div>

                      <div className="flex items-center gap-5 text-sm text-gray-500 font-medium">
                        {getChildAge(child.dateOfBirth)}

                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 mr-[-1px]" />
                          {child.dateOfBirth
                            ? dayjs(child.dateOfBirth).format(DATE_FORMAT_SENTENCE)
                            : 'Date not provided'}
                        </div>
                      </div>
                    </div>
                  </div>
                }
                extra={
                  !readOnly && (
                    <Button
                      icon={<Edit className="mb-[-2px]" />}
                      onClick={() => handleEditChild(index)}
                      data-testid={`edit-child-${index}-button`}
                    >
                      Edit
                    </Button>
                  )
                }
                noBodyPadding
              >
                <List
                  dataSource={getChildDataItems(child)}
                  renderItem={(item) => (
                    <List.Item>
                      <div className="flex flex-col md:flex-row w-full px-7 py-2 gap-1 md:gap-4">
                        <div className="w-full md:w-5/12 lg:w-1/3 font-semibold text-gray-700">{item.label}</div>
                        <div className="w-full md:w-7/12 lg:w-2/3 text-gray-600">{item.value}</div>
                      </div>
                    </List.Item>
                  )}
                />
              </Card>
            ))}

            {!readOnly && (
              <Button
                type="primary"
                icon={<Add className="w-7 h-7 mb-[-2px] ml-[-5px] mr-[-4px]" />}
                size="large"
                onClick={handleAddChild}
                data-testid="add-another-child-button"
              >
                Add another child who needs care
              </Button>
            )}
          </div>
        )}
      </div>

      <ApplicationWizardActions
        backButtonProps={{
          onClick: () => {
            const lastChildIndex = Math.max(0, application?.children?.length - 1);
            const previousStep = getPreviousStep(
              FAMILY_APPLICATION_STEPS.CHILDREN_SUMMARY,
              application,
              stepConfig,
              lastChildIndex,
            );
            setStep({
              applicationId: application?.id,
              newStep: previousStep,
            });
          },
          'data-testid': 'children-summary-back-button',
        }}
        nextButtonProps={{
          text: 'Continue to Eligibility Certification',
          onClick: handleContinue,
          disabled: !application?.children?.length,
          'data-testid': 'children-summary-next-button',
        }}
      />
    </>
  );
}
